import { useEffect, useRef } from 'react';
import { useDebounce } from 'usehooks-ts';

import PlaygroundEditor from '../../components/common/PlaygroundEditor/PlaygroundEditor';
import {
  useEditorStore,
  useTemporalEditorStore,
} from '../../services/store/playground-editor.store';
import { AI_WRITER_EDITOR_LAST_TEXT } from '../../configs';
import { MenuCategoryEnum } from '../../constants/tool-categories';
import useStore from '../../services/store';
import useMenuStore from '../../services/store/menuStore';
import { ResultType } from '../../utils/common/types';

export default function PersistentEditor() {
  const isFirstRender = useRef(true);
  const addRecents = useStore((state) => state.addRecents);
  const addResults = useStore((state) => state.addResults);
  const removeResult = useStore((state) => state.removeResult);

  const selectedMenuSubcategory = useMenuStore((state) => state.selectedMenuSubcategory);

  const { undo, redo, futureStates, pastStates } = useTemporalEditorStore((state) => state);

  const {
    id: currentEditorId,
    text,
    error,
    isOpen,
    writeText,
    toggleOpen,
    setError: clearError,
    setSubcategory,
    currentSubcategory,
  } = useEditorStore((state) => state);

  const debouncedText = useDebounce(text, 600);
  const isSubcategoryInitialized = useRef(false);

  useEffect(() => {
    if (selectedMenuSubcategory !== currentSubcategory) {
      setSubcategory(selectedMenuSubcategory);
      isSubcategoryInitialized.current = true;
    } else if (!isSubcategoryInitialized.current && selectedMenuSubcategory !== undefined) {
      setSubcategory(selectedMenuSubcategory);
      isSubcategoryInitialized.current = true;
    }
  }, [selectedMenuSubcategory, currentSubcategory, setSubcategory]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const storageKey =
      currentSubcategory == null
        ? AI_WRITER_EDITOR_LAST_TEXT
        : `${AI_WRITER_EDITOR_LAST_TEXT}-${currentSubcategory}`;

    sessionStorage.setItem(storageKey, debouncedText);

    if (debouncedText === '') {
      removeResult({ id: currentEditorId, category: MenuCategoryEnum.PLAYGROUND_EDITOR_CATEGORY });
    } else {
      addResults({
        replace: true,
        newResults: [
          {
            id: currentEditorId,
            type: ResultType.Common,
            isSensitive: false,
            result: debouncedText,
            rephrasedResults: [],
            isRephrasing: false,
            category: MenuCategoryEnum.PLAYGROUND_EDITOR_CATEGORY,
          },
        ],
        category: MenuCategoryEnum.PLAYGROUND_EDITOR_CATEGORY,
      });
      addRecents({ category: MenuCategoryEnum.PLAYGROUND_EDITOR_CATEGORY, id: currentEditorId });
    }
  }, [debouncedText, currentSubcategory, addRecents, addResults, currentEditorId, removeResult]);

  return (
    <PlaygroundEditor
      undo={undo}
      redo={redo}
      value={text}
      error={error}
      isOpen={isOpen}
      setValue={writeText}
      onToggle={toggleOpen}
      canDo={pastStates.length > 0}
      canRedo={futureStates.length > 0}
      closeError={() => clearError(null)}
    />
  );
}
